// DateRangePicker.js
import { useEffect, useState } from "react";
import { Calendar } from "primereact/calendar";
import "./DateRangePicker.css";
import { useTranslation } from "../../contexts/TranslationContext";
import dayjs from "dayjs";
import {
  getPrimeReactDateFormat,
  getPrimeReactLocale,
  syncPrimeReactLocale,
} from "../../utils/primeReactLocale";

const DateRangePicker = ({ value, onChange, arrow = false, min }) => {
  const [internalValue, setInternalValue] = useState({
    startDate: null,
    endDate: null,
  });
  const { t, currentLanguage } = useTranslation();
  const calendarLocale = getPrimeReactLocale(currentLanguage);
  const calendarDirection = "ltr";
  const calendarDateFormat = getPrimeReactDateFormat(currentLanguage);

  useEffect(() => {
    syncPrimeReactLocale(currentLanguage);
  }, [currentLanguage]);

  const normalizeDate = (date) => {
    if (!date) return null;

    const parsedDate = date instanceof Date ? date : new Date(date);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  };

  const formatDateForChange = (date) =>
    date ? dayjs(date).format("YYYY-MM-DD") : "";

  const emitChange = (startDate, endDate) => {
    onChange({
      startDate: formatDateForChange(startDate),
      endDate: formatDateForChange(endDate),
    });
  };

  const handleStartDateChange = (e) => {
    const nextStartDate = normalizeDate(e.value);
    const nextEndDate =
      internalValue.endDate &&
      nextStartDate &&
      dayjs(internalValue.endDate).isBefore(nextStartDate, "day")
        ? null
        : internalValue.endDate;

    setInternalValue({
      startDate: nextStartDate,
      endDate: nextEndDate,
    });
    emitChange(nextStartDate, nextEndDate);
  };

  const handleEndDateChange = (e) => {
    const nextEndDate = normalizeDate(e.value);

    setInternalValue((prev) => {
      const nextValue = {
        startDate: prev.startDate,
        endDate: nextEndDate,
      };

      emitChange(nextValue.startDate, nextValue.endDate);
      return nextValue;
    });
  };

  useEffect(() => {
    setInternalValue({
      startDate: normalizeDate(value?.startDate),
      endDate: normalizeDate(value?.endDate),
    });
  }, [value?.startDate, value?.endDate]);

  const minDate = normalizeDate(min);
  const endMinDate = internalValue.startDate || minDate;

  return (
    <div className="custom-range-picker">
      <div className="date-inputs">
        <Calendar
          locale={calendarLocale}
          dir={calendarDirection}
          value={internalValue.startDate}
          onChange={handleStartDateChange}
          readOnlyInput
          minDate={minDate ?? null}
          appendTo="self"
          className="date-range-calendar"
          placeholder={t("StartDate")}
          inputClassName="date-range-input"
          dateFormat={calendarDateFormat}
        />
        {arrow ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="26"
            height="12"
            viewBox="0 0 26 12"
            fill="none"
          >
            <path
              d="M25.5353 6.53033C25.8282 6.23744 25.8282 5.76256 25.5353 5.46967L20.7624 0.696699C20.4695 0.403806 19.9946 0.403806 19.7017 0.696699C19.4088 0.989593 19.4088 1.46447 19.7017 1.75736L23.9443 6L19.7017 10.2426C19.4088 10.5355 19.4088 11.0104 19.7017 11.3033C19.9946 11.5962 20.4695 11.5962 20.7624 11.3033L25.5353 6.53033ZM0 6.75H25.005V5.25H0V6.75Z"
              fill="#D5D5D5"
            />
          </svg>
        ) : (
          <span>{t("To")}</span>
        )}

        <Calendar
          locale={calendarLocale}
          dir={calendarDirection}
          value={internalValue.endDate}
          onChange={handleEndDateChange}
          readOnlyInput
          minDate={endMinDate ?? null}
          appendTo="self"
          className="date-range-calendar"
          placeholder={t("EndDate")}
          inputClassName="date-range-input"
          viewDate={internalValue.startDate || endMinDate || undefined}
          dateFormat={calendarDateFormat}
        />
      </div>
    </div>
  );
};

export default DateRangePicker;
